const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const RESIZE_DEBOUNCE_MS = 100;

class DdTicker extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MediaQueryList | null} */
  #motion = null;

  /** @type {number | null} */
  #resizeTimer = null;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#motion = window.matchMedia(REDUCED_MOTION);
    this.#motion.addEventListener('change', this.#handleMotionChange, {
      signal: this.#abortController.signal,
    });

    this.querySelector('[data-ticker-toggle]')?.addEventListener('click', this.#togglePaused, {
      signal: this.#abortController.signal,
    });
    this.addEventListener('focusin', this.#handleFocusIn, { signal: this.#abortController.signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal: this.#abortController.signal });

    const viewport = this.querySelector('[data-ticker-viewport]');
    const source = this.querySelector('[data-ticker-source]');
    if ('ResizeObserver' in window && viewport && source) {
      this.#resizeObserver = new ResizeObserver(this.#scheduleRebuild);
      this.#resizeObserver.observe(viewport);
      this.#resizeObserver.observe(source);
    }

    this.#applyMotionPreference();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#abortController?.abort();
    this.#abortController = null;
    if (this.#resizeTimer != null) window.clearTimeout(this.#resizeTimer);
    this.#resizeTimer = null;
    this.#clearFocusPosition();
    this.#removeClones();
  }

  #handleMotionChange = () => {
    this.#applyMotionPreference();
  };

  #applyMotionPreference() {
    if (this.#motion?.matches) {
      this.#showStaticSource();
      return;
    }

    const fontsReady = document.fonts?.ready ?? Promise.resolve();
    fontsReady.then(() => {
      if (this.isConnected && !this.#motion?.matches) this.#rebuild();
    });
  }

  #scheduleRebuild = () => {
    if (this.#motion?.matches) return;
    if (this.#resizeTimer != null) window.clearTimeout(this.#resizeTimer);
    this.#resizeTimer = window.setTimeout(() => {
      this.#resizeTimer = null;
      this.#rebuild();
    }, RESIZE_DEBOUNCE_MS);
  };

  #handleFocusIn = (event) => {
    if (!(event.target instanceof HTMLElement) || !event.target.closest('[data-ticker-source]')) return;

    const viewport = /** @type {HTMLElement | null} */ (this.querySelector('[data-ticker-viewport]'));
    const track = /** @type {HTMLElement | null} */ (this.querySelector('.dd-ticker__track'));
    if (!viewport || !track) return;

    this.setAttribute('data-ticker-focus-pinned', '');
    track.style.setProperty('--dd-ticker-focus-offset', '0px');

    const viewportRect = viewport.getBoundingClientRect();
    const targetRect = event.target.getBoundingClientRect();
    const inset = 8;
    let offset = 0;

    if (targetRect.width >= viewportRect.width - inset * 2 || targetRect.left < viewportRect.left + inset) {
      offset = viewportRect.left + inset - targetRect.left;
    } else if (targetRect.right > viewportRect.right - inset) {
      offset = viewportRect.right - inset - targetRect.right;
    }

    track.style.setProperty('--dd-ticker-focus-offset', `${offset}px`);
  };

  #handleFocusOut = () => {
    queueMicrotask(() => {
      if (!this.isConnected || !this.contains(document.activeElement)) this.#clearFocusPosition();
    });
  };

  #clearFocusPosition() {
    this.removeAttribute('data-ticker-focus-pinned');
    const track = /** @type {HTMLElement | null} */ (this.querySelector('.dd-ticker__track'));
    track?.style.removeProperty('--dd-ticker-focus-offset');
  }

  #rebuild() {
    const viewport = /** @type {HTMLElement | null} */ (this.querySelector('[data-ticker-viewport]'));
    const track = /** @type {HTMLElement | null} */ (this.querySelector('.dd-ticker__track'));
    const source = /** @type {HTMLElement | null} */ (this.querySelector('[data-ticker-source]'));
    const toggle = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-ticker-toggle]'));
    if (!viewport || !track || !source || !toggle) return;

    this.#removeClones();
    this.setAttribute('data-ticker-measuring', '');
    const sourceWidth = source.getBoundingClientRect().width;
    this.removeAttribute('data-ticker-measuring');
    if (sourceWidth <= 0 || viewport.clientWidth <= 0) {
      this.#showStaticSource();
      return;
    }

    const cloneCount = Math.ceil(viewport.clientWidth / sourceWidth) + 1;
    for (let index = 0; index < cloneCount; index += 1) {
      const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
      clone.removeAttribute('data-ticker-source');
      clone.setAttribute('data-ticker-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      if (index === 0) clone.setAttribute('data-shine-copy', '');
      this.#sanitizeClone(clone);
      track.append(clone);
    }

    track.style.setProperty('--dd-ticker-distance', `${sourceWidth}px`);
    this.setAttribute('data-ticker-ready', '');
    toggle.hidden = false;
    const paused = this.hasAttribute('data-paused');
    toggle.setAttribute('aria-pressed', String(paused));
    toggle.setAttribute('aria-label', paused ? this.dataset.labelPlay || '' : this.dataset.labelPause || '');
  }

  /** @param {HTMLElement} clone */
  #sanitizeClone(clone) {
    const elements = [clone, ...clone.querySelectorAll('*')];
    for (const element of elements) {
      element.removeAttribute('id');
      for (const attribute of Array.from(element.attributes)) {
        if (attribute.name.startsWith('data-shopify-')) element.removeAttribute(attribute.name);
      }
    }
  }

  #showStaticSource() {
    this.#clearFocusPosition();
    this.#removeClones();
    this.removeAttribute('data-ticker-ready');
    this.removeAttribute('data-ticker-measuring');
    // Keep data-paused and the toggle's pressed/label state — only the visitor
    // control should clear or change pause. Rebuild can restore motion later.
    const track = /** @type {HTMLElement | null} */ (this.querySelector('.dd-ticker__track'));
    track?.style.removeProperty('--dd-ticker-distance');
    const toggle = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-ticker-toggle]'));
    if (toggle) toggle.hidden = true;
  }

  #removeClones() {
    this.querySelectorAll('[data-ticker-clone]').forEach((clone) => clone.remove());
  }

  #togglePaused = () => {
    const toggle = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-ticker-toggle]'));
    if (!toggle) return;
    const paused = this.toggleAttribute('data-paused');
    toggle.setAttribute('aria-pressed', String(paused));
    toggle.setAttribute('aria-label', paused ? this.dataset.labelPlay || '' : this.dataset.labelPause || '');
  };
}

if (!customElements.get('dd-ticker')) {
  customElements.define('dd-ticker', DdTicker);
}
